import React, { useEffect } from 'react';

import { Stack, Typography, CardContent, CardActionArea, CardActions, Button, List, ListItem } from '@mui/material';
import PinDropIcon from '@mui/icons-material/PinDrop';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { LengthBadgeValue } from '../Badge/LengthBadge';
import { AccBadge } from '../Badge/AccBadge';
import { selectFeature } from '../../store/actions/selectedFeatureActions';
import { setContentType } from '../../store/actions/sideBarActions';
import { closePoiListingMenu, enableFlyTo } from '../../store/actions/poiListingActions';
import { setOpenSideBar } from '../../store/actions/sideBarActions';
import { setAlert, hideAlert } from '../../store/actions/alertActions';
import { CardInfoDetails } from '../Card/CardInfoDetails';
import { callGetFeatureJson } from '../../api/jsonFeatureApi';
import { focusElement } from '../../store/actions/focusActions';

const profilAttributeAccessMapping = {
  'Déficience motrice': 'amot',
  'Déficience visuelle': 'avis',
  'Déficience auditive': 'aaud',
  'Déficience mentale': 'amen'
};

function testAreEqual(prevRoad, nextRoad) {
  if (JSON.stringify(prevRoad.poiList) !== JSON.stringify(nextRoad.poiList)) {
    return false;
  }
  return true;
}


const FeatureListingResultComponent = ({ poiList, isMobile }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const getFeatureInfoAttrs = useSelector(state => state.layerConf.getFeatureInfoAttrs);
  const profil = useSelector(state => state.profils.profil);
  const selectedFeaturePoi = useSelector(state => state.selectedFeature.feature);

  const clickCardCallBack = async(feature) => {
    let result = await callGetFeatureJson(feature.properties.layername, feature.properties.fid);
    result['layer'] = {'id': feature.properties.layername};
    dispatch(selectFeature(result, feature.properties.layername));
    if (feature.properties.layername === 'lignes_transports') {
      dispatch(setContentType('selectedTransportDetails'));
    } else {
      dispatch(setContentType('selectedFeatureDetails'));
    }
    dispatch(hideAlert());
    dispatch(focusElement(document.activeElement));
    const violation = document.getElementById('return-button');
    if (violation !== undefined && violation !== null) {
      violation.focus({focusVisible: true});
    }
    if (isMobile) {
      setTimeout(() => {
        dispatch(setOpenSideBar(true));
        dispatch(closePoiListingMenu());
      }, 500);
    }
    return true;
  };

  const handleSeeOnMapClick = async(poi) => {
    if (isMobile) {
      dispatch(hideAlert());
      let result = await callGetFeatureJson(poi.properties.layername, poi.properties.fid);
      result['layer'] = {'id': poi.properties.layername};
      dispatch(selectFeature(result, poi.properties.layername));
    } else {
      await clickCardCallBack(poi);
    }
    dispatch(enableFlyTo());
    setTimeout(() => {
      dispatch(closePoiListingMenu());
    }, 500);
    return;
  };

  useEffect(() => {
    if (poiList !== undefined) {
      if (poiList.length === 0) {
        dispatch(setAlert(t('poiListing.no-result'), 'info', t('info.label'), 'status'));
      } else {
        dispatch(setAlert(t('poiListing.results'), 'success',  t('success.label'), 'status'));
      } 
    } else {
      return;
    }
  }, [poiList]);
  
  return (
    <List
      sx={{
        padding: '1em',
        height: isMobile? '100%': 500,
        backgroundColor: isMobile ? '#E5E5E5': 'white',
        borderRadius: '0.5em',
        overflow: 'auto',
        display:'flex',
        flexDirection:'column',
        paddingX: '0 !important',
        alignItems: 'center', 
        '&::-webkit-scrollbar': {
          width: '4px'
        },
        '&::-webkit-scrollbar-track': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '0.5em',
          backgroundColor: 'gray'
        },
      }}
    >
      {poiList?.length > 0 ? (
        Object.keys(poiList).map(function(index) {
            
          const poi = poiList[index];
          const featureTitleAttr = getFeatureInfoAttrs[poi.properties.layername]?.title;
          const tempTitle = poi.properties[featureTitleAttr];
          const title = tempTitle && tempTitle !== '' ? tempTitle: getFeatureInfoAttrs[poi.properties.layername]?.defaultTitle;
          const accKey = profilAttributeAccessMapping[profil];
  
          var address = null;
          if (poi.properties.adresse1 || poi.properties.commune) {
            address = `${poi.properties.adresse1}, ${poi.properties.commune}`;
          }
          return (
            <ListItem data-cy='poi_listing_results' key={index} sx={{ width: '100%'}}>
              <CardInfoDetails sx={{width: '100%'}} active={selectedFeaturePoi.properties?.fid === poi.properties.fid ? 'true' : 'false'}>
                <Stack direction="column" spacing={1}>
                  <CardActionArea onClick={() => clickCardCallBack(poi)}>
                    <CardContent>
                      <Typography component="h3" style={{fontFamily: 'Luciole-bold'}}>
                        {poi.properties.activite ? `${poi.properties.activite}: ` : ''} {title}
                      </Typography>
                      <Stack direction="row" spacing={1} style={{alignItems:'center', paddingTop: '1em'}} sx={{ flexWrap: 'wrap' }}>
                        {address &&
                            <Stack direction="row" spacing={1} style={{alignItems:'center'}}>
                              
                              <PinDropIcon sx={{color: 'black', fontSize: '1.2rem'}}/>
                              <Typography component="p">
                                {address}
                              </Typography>
                            </Stack>
                        }
                        <LengthBadgeValue value={poi.properties.distance}/>
                        <AccBadge acc={poi.properties[accKey]}/>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                  <CardActions>
                    <Button size="small" onClick={()=>handleSeeOnMapClick(poi)}>
                      {t('poiListing.see-on-map')}
                    </Button>
                  </CardActions>
                </Stack>
              </CardInfoDetails>
            </ListItem>
          );
        })
      ):(
        <Typography component="p" style={{fontFamily: 'Luciole'}}>
          {t('poiListing.no-result')}
        </Typography>
      )
      }
    </List>
  );
};

export const FeatureListingResult = React.memo(FeatureListingResultComponent, testAreEqual);